import {PokerMap} from './Map'
import {Filter} from '../filter/LobbyFilters'
import { GameType } from '../LobbyManager'

/**
 * Handles lobby data (for tables/tournaments)
 * Automatically merges existing items with snapshot updates
 * @type {LobbyData}
 */
export class LobbyData {

	notifyUpdate : boolean
	items : PokerMap<Game>
	validator : LobbyDataValidator
	filters : () => Filter[]
	onUpdate : (items: Game[]) => {}

	sorters: Array<Sort<Game>> = []
	customSort: Sort<Game> | undefined = undefined

	constructor(validator, filters, onUpdate){
		this.notifyUpdate = false
		this.items = new PokerMap<Game>()
		this.validator = validator
		this.filters = filters
		this.onUpdate = onUpdate
	}

	isAllowedByFilters(data: Game, filters: Filter[]) {
		for (var i = 0; i < filters.length; i++) {
			var filter = filters[i]
			if (filter.filter(data) == false) {
				return false
			}
		}
		return true
	}

	updateItems(items: Game[]) {
		var requiresRedraw = false
		for (var i = 0; i<items.length; i++) {
			var item = items[i]
			var current = this.items.get(item.id)
			if (current!=null) {
				current = this._update(current,item)
				this.items.put(item.id,current)
				if (this.validator.shouldRemoveItem(current)) {
					requiresRedraw = true
				} else if (requiresRedraw==false) {
					requiresRedraw = this._requiresRedraw(item)
				}
			}
		}
		if (requiresRedraw==true || this.notifyUpdate==true) {
			this.onUpdate(this.getFilteredItems())
		}
	}

	performSort() {
		this.onUpdate(this.getFilteredItems())
	}

	addItems(items: Game[]) {
		for (var i = 0; i<items.length; i++) {
			this.addItem(items[i])
		}
		if (this.notifyUpdate==true) {
			this.onUpdate(this.getFilteredItems())
		}
	}

	remove(id: number | string) {
		var removed = this.items.remove(id)
		if (removed!=null) {
			this.onUpdate(this.getFilteredItems())
		}
	}

	clear() {
		this.items = new PokerMap<Game>()
		this.notifyUpdate = false
	}

	_requiresRedraw(item: Game) {
		if ('registered' in item) {
			const tournament = (item as Tournament)
			return (tournament.registered != null && tournament.status != null)
		}

		if ('seated' in item) {
			return (item as RingGame).seated != null
		}
		return false
	}
	/**
     * @param item.id
     * @param [item.showInLobby]
     */
	addItem(item: Game) {
		if (typeof(item.id)==='undefined') return

		var current = item
		this.items.put(item.id,current)

		if (this.validator.validate(current)) {
			this.notifyUpdate = true
			this.onUpdate(this.getFilteredItems())
		}
	}
	_update(current,update) {

		for (var x in current) {
			if (typeof(update[x])!=='undefined' && update[x]!=null) {
				current[x] = update[x]
			}
		}

		return current
	}
	/**
     * Returns items that passes the LobbyDataValidator
     * validation step
     * @return {Array}
     */
	getFilteredItems(): Game[] {
		var filters = this.filters()
		for (var i = 0; i<filters.length; i++) {
			if (typeof(filters[i].reset)!=='undefined') {
				filters[i].reset()
			}
		}
		var items = this.items.values()
		var filtered = []
		for (var i = 0; i<items.length; i++) {
			if (!this.validator.shouldRemoveItem(items[i]) && this.validator.validate(items[i])==true) {
				if (this.isAllowedByFilters(items[i], filters)) {
					filtered.push(items[i])
				}
			}
		}

		if (this.sorters.length>0) {
			filtered = this.sort(filtered)
		}

		return filtered
	}
	sort(items: Array<RingGame | SitAndGo>) {
		return this.customSort
			? sort(items, [...this.sorters, this.customSort])
			: sort(items, this.sorters)
	}
	getItem(id) {
		return this.items.get(id)
	}
	getCurrentSort() {
		return this.sorters[this.sorters.length-1]
	}
	refresh() {
		this.onUpdate(this.getFilteredItems())
	}
}

/**
 * @type {LobbyDataValidator}
 */
export class LobbyDataValidator{
	/**
     * @param item
     * @return {Boolean}
     */
	validate (item) {
		return true
	}
	shouldRemoveItem(item) {
		return item.showInLobby!=null && item.showInLobby == 0
	}
}

/**
 * @type {TableLobbyDataValidator}
 * @extends {LobbyDataValidator}
 */
export class TableLobbyDataValidator extends LobbyDataValidator {
	constructor() {
		super()
	}
	validate(item) {
		return item.name!=null && item.capacity!=null
	}

}

/**
 * @type {TournamentLobbyDataValidator}
 * @extends {LobbyDataValidator}
 */
export class TournamentLobbyDataValidator extends LobbyDataValidator {
	constructor() {
		super()
	}
	validate(item) {
		return item.name!=null && item.capacity!=null && item.status!=null && item.buyIn!=null 
	}
	shouldRemoveItem(item) {
		return super.shouldRemoveItem(item) || (item.status!=null && item.status == 'CLOSED')
	}

}

export class SurgeDataValidator extends LobbyDataValidator {
	constructor() {
		super()
	}
	validate(item) {
		return true
	}
}


export function sort<T>(items: T[], sorters: Array<Sort<T>>): T[] {
	let result = [...items]

	for (let s of sorters) {
		result = s(result)
	}

	return result
}

export type Sort<T> = (items: T[]) => T[];

export type RingGame = Game & {
    name: string;
    capacity: number;
    smallBlind: string;
    bigBlind: string;
    ante: number;
    seated: number;
    level: number;
    handsPerHour: number;
    averagePot: string;
    flopsSeen: number;
    minBuyIn?: string;
    maxBuyIn?: string;
    isQuickSeat?: boolean;
    isQuickSeatTable?: boolean;
		features?: RingGameFeature[] | undefined;
};

export type SitAndGo = Game & {
    buyIn: string;
    fee: string;
    registered: number;
    buyInCurrencyCode: string;
    status: Status;
		tournamentCloseTime?: string;
		playEndTime: string;
};

export type Tournament = Game & {
    buyIn: string;
    fee: string;
    bountyFee: string;
    mysteryBountyFee: string;
    registered: number;
    registrationOpen: boolean;
    startTime: string;
    status: Status;
};

export type Surge = Game & RingGame & {
    surgeId: string;
    minBuyIn: string,
    maxBuyIn: string,
};

export type Game = {
    id: number | string;
		gameType: GameType;
    capacity: number;
    name: string;
    ruleDescription: string;
    locked: boolean;
    type: string;
    variant: string;
    currencyCode: 'XSD' | 'XGC' | undefined;
};

export const enum Status {
    ANNOUNCED = 'ANNOUNCED',
    CANCELLED = 'CANCELLED',
    CLOSED = 'CLOSED',
    FINISHED = 'FINISHED',
    ON_BREAK = 'ON_BREAK',
    PREPARING_BREAK = 'PREPARING_BREAK',
    REGISTERING = 'REGISTERING',
    RUNNING = 'RUNNING',
}

export const enum RingGameFeature {
		AllInOrFold = 'AllInOrFold',
		BadBeatJackpot = 'BadBeatJackpot'

}
